/**
 * Строка состояния дня: «сколько ещё сидеть», одним предложением.
 *
 * До 20 сентября 2026 на этот вопрос отвечала дневная полоса — засечка
 * минимума, засечка цели и подпись под ними. Полосы с главного экрана ушли
 * (три из них занимали треть первого экрана и ни одна не вела к действию), а
 * вопрос остался: он единственный из всего, что полосы говорили, меняет
 * решение прямо сейчас — продолжать или на сегодня хватит.
 *
 * Поэтому порог остался словами, а не шкалой. Слова тут даже честнее: полоса
 * выглядит мерой выполнения нормы, а нормы нет — минуты набора это оценка
 * времени (решение от 2 сентября 2026). «До набора 6 минут» обещает столько
 * же и ничего не запирает.
 *
 * Все три числа считает сервер, `today.plan().meter`: своя оценка на экране
 * разъехалась бы с той, по которой планировщик собирал набор.
 */
import { plural } from "./dates";

export type DayState = {
  spentMinutes: number;
  goalMinutes: number;
  /** Минимум короткого дня. `null`, когда он совпал бы с целью. */
  minMinutes: number | null;
  /** Незакрытых задач в наборе. */
  tasksLeft: number;
};

const mins = (n: number) => `${n} ${plural(n, "минута", "минуты", "минут")}`;

export function dayLine({
  spentMinutes,
  goalMinutes,
  minMinutes,
  tasksLeft,
}: DayState): string {
  // Набор пуст — до выбора навыка в нём нечего делать. Порогов тогда нет
  // вовсе: цель 0, а минимум 8 был бы порогом ВЫШЕ обещания, то есть
  // требованием, которого система не выдвигала.
  if (goalMinutes <= 0) return "Набор соберётся, когда выберешь новый навык";
  // Задачи кончились раньше минут — обычное дело у того, кто решает быстро.
  // Сказать ему «до набора 4 минуты» значило бы позвать сидеть над пустым
  // набором: оценка времени промахнулась, а не ученик недоработал.
  if (tasksLeft === 0) {
    return spentMinutes > goalMinutes
      ? `Набор пройден · сверх оценки ${mins(spentMinutes - goalMinutes)}`
      : "Набор пройден · дальше по желанию";
  }
  // Минимум существует, только если он ниже цели. В коротком дне он равен ей,
  // и сервер присылает `null`; страховка ниже — на случай набора короче
  // восьми минут, который планировщик собрать может.
  const hasMin = minMinutes !== null && minMinutes < goalMinutes;
  if (hasMin && spentMinutes < (minMinutes ?? 0)) {
    return `До минимума ${mins((minMinutes ?? 0) - spentMinutes)}`;
  }
  if (spentMinutes < goalMinutes) {
    // «Минимум взят» — только когда минимум есть и он позади. У набора ровно
    // в восемь минут средней зоны нет, и на нуле эта фраза была бы неправдой.
    return `${hasMin ? "Минимум взят · до набора" : "До набора"} ${mins(goalMinutes - spentMinutes)}`;
  }
  // Оценка позади, задачи остались. До 25 сентября 2026 здесь стояло «Набор
  // закрыт по времени», и ученик прочёл это как замок: решил, что дальше
  // нельзя, хотя ничего не запиралось. Строка называет обе правды — оценку
  // прошли, задачи ждут, — и решение «хватит или дальше» оставляет ему.
  const left = `в наборе ещё ${tasksLeft} ${plural(tasksLeft, "задача", "задачи", "задач")}`;
  return spentMinutes > goalMinutes
    ? `Сверх оценки ${mins(spentMinutes - goalMinutes)} · ${left}`
    : `Оценка времени пройдена · ${left}`;
}
